import {
  gelGetSupportedPlatforms, gelListConnectors, gelCreateConnector, gelTestConnector,
  listDpgRules, toggleDpgRule, getMyEntitlements,
  listIntentTemplates, listIntentTemplateVersions, createIntentTemplateDraft,
  submitIntentTemplateVersion, bindAgentToIntentTemplateVersion,
  getUsers, getGroups, getAssignments, createAssignment,
  claimOwnership, attestOwnership,
  gelDiscoverCapabilities, gelUpdateSkillMappings, gelMaterializePolicies,
  manualAutonomyOverride,
  getAgentSpiffe,
} from './api';
import { trustLevelFor } from '../data/protectionLimits';
import { typeLabel } from '../components/v2/frameworkCatalog';

/**
 * What Prove7 Protect is allowed to call, and the shape it needs the answers in.
 *
 * <h3>A connection is chosen, never conjured</h3>
 *
 * The console's registration wizard will not continue until a Platform
 * Connector exists for the framework, and sends the operator off to configure
 * one. Protect used to create one silently, which is a second way to make a
 * platform-level object from a tenant-facing screen — the exact divergence that
 * ends with two surfaces disagreeing about what a customer has.
 *
 * So Protect shows what this caller can actually use: the connections Prove7
 * shares into their tenant, and the ones their tenant owns. Adding their own
 * goes through the same `POST /gel/connectors` the console posts to, and is
 * proved with the same health check before anything is bound to it. Binding an
 * agent to a connection nobody has ever called is how a registration succeeds
 * and every run after it fails.
 *
 * <h3>Boundaries are rules that already exist</h3>
 *
 * "What must never leave" is not a new vocabulary invented for this screen. It
 * is the tenant's data classification rules — listed, and switched on. A rule
 * authored here would be a rule the Data Protection surface does not know
 * about, and the boundary would mean two different things in two places.
 */

const ACTIVE = 'ACTIVE';

/** The platform type as a person says it. Derived, so it cannot drift. */
export function frameworkLabel(platformType) {
  const t = String(platformType || '');
  if (!t) return '';
  /* The catalog is the platform's own naming — the same rows the registration
     wizard's framework picker is built from — so OPENAI reads "OpenAI" and
     VERTEX_AI reads "Google Vertex AI" on both screens. This used to title-case
     the enum here instead, which gave Protect a second, worse spelling of every
     vendor's name. */
  const known = typeLabel(t);
  if (known && known !== t) return known;
  /* A platform type the catalog has not been taught yet. Title-casing it is
     still better than putting a raw enum in front of a tenant. */
  return t.toLowerCase().split('_').filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

const rows = (res) => {
  if (Array.isArray(res)) return res;
  const d = res?.data;
  return Array.isArray(d) ? d : [];
};

/** The response body, whether the client wrapped it in `data` or not. */
const body = (res) => (res && res.data !== undefined ? res.data : res);

/**
 * Every framework the backend supports, and the live connections behind each.
 *
 * `provided` are connections shared into this tenant — Prove7's. `own` are the
 * tenant's. The flag is the API's own `isOwner`, flattened by the client; it is
 * not inferred from a name.
 */
export async function readConnections() {
  try {
    const [platformsRes, connectorsRes] = await Promise.all([
      gelGetSupportedPlatforms(),
      gelListConnectors(),
    ]);
    const types = rows(platformsRes).map((t) => (typeof t === 'string' ? t : t?.platformType))
      .filter(Boolean);
    const live = rows(connectorsRes).filter((c) => c?.status === ACTIVE);

    const frameworks = types.map((platformType) => {
      const mine = live.filter((c) => c.platformType === platformType);
      return {
        platformType,
        label: frameworkLabel(platformType),
        /* `__isOwner === false` means this connection belongs to another tenant
           and was shared in — which, for a customer, is Prove7's. */
        provided: mine.filter((c) => c.__isOwner === false),
        own: mine.filter((c) => c.__isOwner !== false),
      };
    });
    return { frameworks, error: null };
  } catch (e) {
    /* Said out loud. Rendering a failed read as "no connections configured"
       tells somebody their estate is empty, which is a different claim. */
    return { frameworks: [], error: e?.message || 'Could not read your connections.' };
  }
}

/**
 * Connect the customer's own framework, through the platform's connector API,
 * and prove it before its id is handed back to be bound.
 */
export async function connectOwn({
  platformType, name, endpointUrl, authMethod = 'API_KEY', apiKey, description,
}) {
  const key = String(apiKey || '').trim();
  const res = await gelCreateConnector({
    platformType,
    name,
    endpointUrl,
    authMethod,
    /* An empty box is not a credential. Sending `{apiKey: ''}` would store a
       blank secret and fail at the first call with a confusing 401. */
    authConfig: key ? { apiKey: key } : {},
    description: description || `${name} — ${frameworkLabel(platformType)} agent framework`,
  });
  if (res && res.ok === false) {
    throw new Error(res.error || res.message || 'The connection was refused.');
  }
  const created = (res && 'data' in res) ? res.data : res;
  const id = created?.id;
  if (!id) throw new Error('The connection was created but came back without an id.');

  const health = await gelTestConnector(id).catch((e) => ({ data: {
    status: 'UNHEALTHY', message: e?.message || 'The health check could not be run.',
  } }));
  const h = (health && 'data' in health) ? health.data : health;
  return { id, name, health: h?.status || 'UNKNOWN', message: h?.message || null,
    latencyMs: h?.latencyMs ?? null };
}

/**
 * The tenant's data classification rules, grouped by what they protect, with
 * whether that category is already switched on.
 */
export async function readBoundaries() {
  try {
    const all = rows(await listDpgRules());
    const order = [];
    const byCategory = new Map();
    all.forEach((r) => {
      const category = r?.category || 'CUSTOM';
      if (!byCategory.has(category)) { byCategory.set(category, []); order.push(category); }
      byCategory.get(category).push(r);
    });
    const categories = order.map((category) => {
      const group = byCategory.get(category);
      return {
        category,
        label: group[0]?.name || category,
        ruleIds: group.map((r) => r.id),
        /* On when every rule behind it is on — a category half-enforced is not
           a boundary, and saying it is would be the lie this product cannot
           afford. */
        on: group.every((r) => r.enabled === true),
        blocking: group.some((r) => r.action === 'BLOCK'),
      };
    });
    return { categories, error: null };
  } catch (e) {
    return { categories: [], error: e?.message || 'Could not read your boundary rules.' };
  }
}

/** Switch on exactly the rules chosen, and say which would not switch. */
export async function applyBoundaries(ruleIds) {
  const ids = Array.isArray(ruleIds) ? ruleIds.filter(Boolean) : [];
  const applied = [];
  const failed = [];
  /* One at a time, in the order chosen: the result says which rules are on,
     and a parallel burst would make a partial failure unreadable. */
  for (const id of ids) {
    try {
      const res = await toggleDpgRule(id, true);
      if (res && res.ok === false) failed.push(id); else applied.push(id);
    } catch {
      failed.push(id);
    }
  }
  return { applied, failed };
}

/**
 * The allowance behind a Prove7-provided connection.
 *
 * It is a quota on the customer's own plan, so it is read rather than written
 * into the copy. When the plan carries no LLM quota this returns null and the
 * screen says nothing about limits — a word like "free" that nobody can check
 * is worse than no claim at all.
 */
export async function readProvidedAllowance() {
  try {
    const res = await getMyEntitlements();
    const body = (res && 'data' in res) ? res.data : res;
    const quotas = Array.isArray(body?.quotas) ? body.quotas : [];
    const llm = quotas.find((q) => q?.key === 'LLM_COST_USD');
    if (!llm || llm.hard == null) return null;
    return { limit: llm.hard, unit: llm.unit || null, period: llm.period || null };
  } catch {
    return null;
  }
}

/* ── the intent an agent is bound to ───────────────────────────────────── */

/**
 * The intents this tenant can actually bind to.
 *
 * A binding is refused unless the template version is APPROVED, and the
 * Execution-Ready gate checks that a binding exists. A template is bindable
 * when approval has pointed it at an active version — approving sets
 * `activeVersionId` and moves the template to ACTIVE — so that pointer is the
 * question asked here. Offering anything else would offer a bind the platform
 * will reject.
 */
export async function readBindableIntents() {
  try {
    const all = rows(await listIntentTemplates());
    const intents = all
      .filter((t) => t?.activeVersionId && String(t.state).toUpperCase() === 'ACTIVE')
      .map((t) => ({
        templateId: t.id,
        versionId: t.activeVersionId,
        label: t.displayName || t.name,
        description: t.description || null,
        riskTier: t.riskTier || null,
      }));
    return { intents, error: null };
  } catch (e) {
    /* Said out loud. A caller without the permission to list templates has not
       got an estate with no intents in it. */
    return { intents: [], error: e?.message || 'Could not read your intents.' };
  }
}

/** Bind the agent to an approved version, or report the platform's refusal. */
export async function bindIntent(agentId, templateVersionId) {
  const res = await bindAgentToIntentTemplateVersion(agentId, templateVersionId);
  if (res && res.ok === false) {
    throw new Error(res.error || res.message || 'The binding was refused.');
  }
  const body = (res && 'data' in res) ? res.data : res;
  return { bindingId: body?.id || null, templateVersionId };
}

/**
 * Draft a new intent from what somebody described, and put it in the queue.
 *
 * It does not come back bindable and must never be reported as though it did.
 * A new version needs an authored Rego and an approver who is not its author —
 * enforced in the service and again by a database CHECK — so the most this can
 * do is create the draft, submit it for review, and say who has to act next.
 */
export async function proposeIntent({ name, displayName, description, riskTier,
  category, orgId, tenantId }) {
  const created = await createIntentTemplateDraft({
    scopeLevel: 'TENANT',
    orgId,
    tenantId,
    name,
    displayName: displayName || name,
    description,
    riskTier: riskTier || 'MEDIUM',
    category: category || null,
  });
  if (created && created.ok === false) {
    throw new Error(created.error || created.message || 'The intent could not be drafted.');
  }
  const template = (created && 'data' in created) ? created.data : created;
  const templateId = template?.id;
  if (!templateId) throw new Error('The intent was drafted but came back without an id.');

  /* Create records the template and its first DRAFT version, but answers with
     the template alone — so the version to submit is read back. */
  const versions = rows(await listIntentTemplateVersions(templateId).catch(() => null));
  const draft = versions.find((v) => String(v?.state).toUpperCase() === 'DRAFT') || versions[0];
  if (!draft?.id) {
    return { templateId, versionId: null, submitted: false, bindable: false,
      problem: 'The draft was created but its first version could not be read back.' };
  }

  const sent = await submitIntentTemplateVersion(draft.id).catch((e) => ({
    ok: false, error: e?.message }));
  const submitted = !(sent && sent.ok === false);
  return {
    templateId,
    versionId: draft.id,
    submitted,
    /* Never true here. Approval is somebody else's act. */
    bindable: false,
    problem: submitted ? null : (sent.error || 'It was drafted but not submitted for review.'),
  };
}

/* ── who may run it ────────────────────────────────────────────────────── */

/** The access level that names an approver rather than an operator. */
export const APPROVE = 'approve';

/**
 * The levels the model defines, in the order it lists them.
 *
 * `approve` is last and apart on purpose: under segregation of duties it is an
 * approval DUTY, not access. The readiness gate counts only the others, so an
 * agent whose single grant is `approve` has nobody able to run it.
 */
export const ACCESS_LEVELS = [
  { value: 'view', label: 'View — can see it and what it did' },
  { value: 'execute', label: 'Execute — can run it' },
  { value: 'manage', label: 'Manage — can run and change it' },
  { value: 'admin', label: 'Admin — full control of this agent' },
  { value: APPROVE, label: 'Approve — decides its human-approval gates, cannot run it' },
];

/** The people and groups this tenant can grant to. */
export async function readTeam({ orgId, tenantId } = {}) {
  try {
    const [usersRes, groupsRes] = await Promise.all([
      getUsers({ orgId, tenantId }),
      getGroups({ orgId, tenantId }),
    ]);
    return {
      users: rows(usersRes).map((u) => ({
        id: u.id, label: u.name || u.email || u.id, email: u.email || null,
      })),
      groups: rows(groupsRes).map((g) => ({ id: g.id, label: g.name || g.id })),
      error: null,
    };
  } catch (e) {
    /* A caller who cannot read the directory has not got an empty one. */
    return { users: [], groups: [], error: e?.message || 'Could not read your team.' };
  }
}

/**
 * What is granted on this agent, split the way the readiness gate splits it.
 *
 * `satisfied` answers the gate's own question — at least one grant that is not
 * an approval duty — so the surface and the gate cannot disagree.
 */
export async function readAccess(agentId) {
  try {
    const all = rows(await getAssignments(agentId));
    const approvers = all.filter((a) => String(a.accessLevel).toLowerCase() === APPROVE);
    const runtime = all.filter((a) => String(a.accessLevel).toLowerCase() !== APPROVE);
    return { runtime, approvers, satisfied: runtime.length > 0, error: null };
  } catch (e) {
    return { runtime: [], approvers: [], satisfied: false,
      error: e?.message || 'Could not read who has access.' };
  }
}

/** Grant one person or one group a level on this agent. */
export async function grantAccess(agentId, { userId, groupId, accessLevel }) {
  if (!userId && !groupId) {
    throw new Error('A grant has to name a person or a group.');
  }
  if (!ACCESS_LEVELS.some((l) => l.value === accessLevel)) {
    throw new Error(`"${accessLevel}" is not an access level this platform has.`);
  }
  /* Exactly one subject per grant: the backend takes userId or groupId, and
     sending both would leave which one was granted to the server to guess. */
  const payload = userId ? { userId, accessLevel } : { groupId, accessLevel };
  const res = await createAssignment(agentId, payload);
  if (res && res.ok === false) {
    throw new Error(res.error || res.message || 'The grant was refused.');
  }
  const body = (res && 'data' in res) ? res.data : res;
  return { assignmentId: body?.id || null, accessLevel };
}

/**
 * The ownership states a person can actually do something about from here.
 *
 * <h3>Why this is a list and not a judgement each row makes</h3>
 *
 * `OwnershipConsentService.claimOwnership` accepts exactly one state — it reads
 * the agent's live record and refuses with 409 `not_claimable` for anything that
 * is not DISCOVERED_UNOWNED (`:86-90`). So an action offered on any other state
 * is a button whose only possible outcome is an error.
 *
 * The three that are deliberately absent, and what each would need:
 *
 * - NOMINATED — a nudge to the person who has not accepted yet. There is no
 *   endpoint; OwnershipController exposes claim, attest and invite-owner, and
 *   nothing that reminds.
 * - ORPHANED — the owner no longer exists, which is the state that most needs a
 *   re-assignment, and the one with no path. `OwnershipRecordService.applyTransition`
 *   could carry it, but it has no HTTP surface.
 * - RETIRED — terminal by design. Claim refuses it, and should.
 *
 * Each of those still SAYS its state on the row, because the fact is true and
 * worth knowing. What it no longer does is invite a click that cannot complete.
 */
export const CLAIMABLE = 'DISCOVERED_UNOWNED';

/**
 * Name the accountable owner for an agent nobody answers for.
 *
 * <h3>Why "myself" still sends a principal</h3>
 *
 * `claimOwnership` refuses a null owner with 400 `owner_required` (`:75-77`):
 * the ceremony names a person, and "me" is a person. So the caller's own id is
 * sent, and then attested in the same breath — a nomination of oneself that was
 * left un-accepted would park the agent in NOMINATED, waiting on the person who
 * is already here.
 *
 * Nominating somebody else stops at NOMINATED on purpose. Accountability is
 * accepted, never assigned: the named owner accepts through their own tasks
 * list, which is the only place `attest` will take it from (it is owner-only,
 * backend-enforced).
 */
export async function nominateOwner(agentId, intent, selfPrincipalId) {
  const self = intent && intent.ownerMode === 'self';
  const ownerPrincipalId = self ? selfPrincipalId : (intent && intent.ownerPrincipalId) || null;

  if (!agentId) return { ok: false, error: 'No agent to nominate an owner for.' };
  if (!ownerPrincipalId) {
    return {
      ok: false,
      error: self
        ? 'We could not tell who you are signed in as, so there is nobody to record.'
        : 'Choose the person who will answer for this agent.',
    };
  }

  try {
    await claimOwnership(agentId, ownerPrincipalId);
    if (self) {
      /* Accepted here because the person accepting is the person asking. A
         failure to attest leaves a real NOMINATED record, which is a weaker
         outcome than intended but not a wrong one — so it is reported without
         undoing the nomination. */
      try {
        await attestOwnership(agentId);
        return { ok: true, state: 'ACTIVE', error: null };
      } catch (e) {
        return { ok: true, state: 'NOMINATED', error: e?.message || null };
      }
    }
    return { ok: true, state: 'NOMINATED', error: null };
  } catch (e) {
    return { ok: false, error: e?.message || 'The nomination was refused.' };
  }
}

/* ── What the agent can do, and what it may do ───────────────────────────── */

/**
 * Ask the platform what this external agent exposes.
 *
 * <h3>Why an empty answer is not a failure</h3>
 *
 * Not every platform supports introspection. The backend says so in its own
 * words and returns an empty list rather than an error, so an agent on a
 * platform that cannot be asked is still protectable — its capabilities get
 * named by hand instead. Reporting that as a failure would stop a journey with
 * nothing wrong with it.
 */
export async function readCapabilities(registrationId) {
  try {
    const found = body(await gelDiscoverCapabilities(registrationId));
    const list = Array.isArray(found?.capabilities) ? found.capabilities : [];
    return {
      capabilities: list.map((c) => ({
        name: c.name,
        description: c.description || '',
        riskLevel: c.riskLevel || null,
        suggestedTags: Array.isArray(c.suggestedTags) ? c.suggestedTags : [],
      })),
      message: found?.message || null,
      error: null,
    };
  } catch (e) {
    return {
      capabilities: [],
      message: null,
      error: e?.message || 'Could not ask this agent what it can do.',
    };
  }
}

/**
 * Record which of those capabilities this agent is allowed to use.
 *
 * <h3>Why everything else waits on this step</h3>
 *
 * Protection binds to capabilities — one proxy skill per mapping.
 * `materializeProxySkills` returns an empty list when there are no mappings,
 * and `materializePolicyConfigurations` refuses outright without at least one
 * proxy skill. So an agent with nothing mapped cannot have a single rule
 * enforced on it, however many limits are set afterwards.
 *
 * The platform materializes the proxies inside this same call, so this is one
 * request and not two.
 */
export async function mapCapabilities(registrationId, chosen) {
  const mappings = (Array.isArray(chosen) ? chosen : [])
    .filter((c) => c && c.name)
    .map((c) => ({
      name: c.name,
      description: c.description || '',
      ...(c.category ? { category: c.category } : {}),
    }));

  if (!mappings.length) {
    return { ok: false, mapped: 0, error: 'Choose at least one thing this agent may do.' };
  }
  try {
    await gelUpdateSkillMappings(registrationId, mappings);
    return { ok: true, mapped: mappings.length, error: null };
  } catch (e) {
    return { ok: false, mapped: 0, error: e?.message || 'Could not record what this agent may do.' };
  }
}

/**
 * Put this agent's limits in force.
 *
 * <h3>One answer about autonomy, two writes</h3>
 *
 * "How much may it do on its own" is two objects here: the enforcement cap the
 * gateway checks on every protected call, and the trust ceiling that satisfies
 * the Decide gate. Two vocabularies, at different heights — which is why the
 * pairing lives in `protectionLimits.AUTONOMY_CHOICES`, asserted against both
 * backend ladders, rather than being decided in this function.
 *
 * <h3>Rules first, ceiling second</h3>
 *
 * If the ceiling fails afterwards the agent is protected but will not pass its
 * gate: a partial state somebody can see and retry. The other order would raise
 * a ceiling on an agent with no rules behind it, which is the failure that
 * matters.
 */
export async function applyProtectionLimits(registrationId, agentId, limits) {
  const governancePolicies = {
    dataClassification: limits.dataClassification,
    maxAutonomyLevel: limits.maxAutonomyLevel,
    dpgScanLevel: limits.dpgScanLevel,
    requiredApprovals: Number(limits.requiredApprovals) || 0,
    rateLimitPerMinute: Number(limits.rateLimitPerMinute) || 60,
  };

  let rules = 0;
  try {
    const result = body(await gelMaterializePolicies(registrationId, governancePolicies));
    rules = result?.materializedCount ?? 0;
  } catch (e) {
    return { ok: false, rules: 0, ceiling: null, error: e?.message || 'Could not put these limits in force.' };
  }

  if (!agentId) {
    return { ok: true, rules, ceiling: null, error: null };
  }

  const ceiling = trustLevelFor(limits.maxAutonomyLevel);
  try {
    await manualAutonomyOverride(agentId, ceiling, 'Ceiling set while protecting this agent');
    return { ok: true, rules, ceiling, error: null };
  } catch (e) {
    /* The rules are in force; only the ceiling is missing. Reporting a failure
       would send somebody back to re-enter limits that already landed — and
       handing back the platform's bare words ("403 Access denied") would do the
       same thing more quietly, because nothing in them says half of this
       worked. So the sentence leads with what landed, and carries the
       platform's reason rather than replacing it. */
    const why = e?.message ? ` ${e.message}` : '';
    return {
      ok: true,
      rules,
      ceiling: null,
      error: `This agent's limits are in force, but its autonomy ceiling was not set, `
        + `so it will not pass its gate yet.${why}`,
    };
  }
}

/**
 * The identity this agent was given, read back from the platform.
 *
 * <h3>Why the stage shows it instead of asserting it</h3>
 *
 * "Stamped it with a name of its own" is a claim. The SPIFFE name, the trust
 * domain it belongs to, and the AI-system claims carried in the SVID extension
 * are the evidence for it, and they are what the platform's own Identity tab
 * shows. A stage whose definition is "SPIFFE/SVID issued; cryptographic name
 * stamped" should put that on screen, not summarise it in a sentence.
 *
 * <h3>Honest about the certificate</h3>
 *
 * The name is minted at registration; the X.509 SVID is issued when the
 * workload first presents itself, so `svidActive` is false on a freshly
 * registered agent. That is a NORMAL state, not a failure, and it is reported
 * as "not yet presented" rather than dressed up as provisioned.
 */
export async function readIdentity(agentId) {
  if (!agentId) return { error: 'No agent to read an identity for.' };
  try {
    const d = body(await getAgentSpiffe(agentId));
    const m = d?.aiSystemMetadata || {};
    return {
      spiffeId: d?.spiffeId || null,
      trustDomain: d?.trustDomain || null,
      svidActive: Boolean(d?.svidActive),
      svidSerial: d?.svidSerial || null,
      svidExpiresAt: d?.svidExpiresAt || null,
      /* Named for what they are on the wire. The platform writes these into the
         SVID extension; renaming them here would make two vocabularies for one
         set of claims. */
      claims: {
        kind: m.aiSystemKind || null,
        autonomy: m.aiSystemAutonomy || null,
        riskTier: m.aiSystemRiskTier || null,
        policyCount: typeof m.aiSystemPolicyCount === 'number' ? m.aiSystemPolicyCount : null,
        platformVersion: m.aiSystemPlatformVersion || null,
      },
      error: null,
    };
  } catch (e) {
    /* Said out loud: a refusal or an outage is not "this agent has no identity",
       which is a statement nobody should read from a blank panel. */
    return { error: e?.status === 403
      ? 'You do not have permission to read this agent\'s identity.'
      : (e?.message || 'Could not read this agent\'s identity just now.') };
  }
}
